import { AxiosResponse } from "axios";
import * as React from "react";
import {
  CloudDomainService,
  IStartDomainResponse,
  IVerifyDomainResponse
} from "../../../../services/cloud-domain-service";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCopyButton from "../../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallSpinner from "../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaCodeText from "../../../ui-kit/typography/CodeText/CodeText";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaH6 from "../../../ui-kit/typography/H6/H6";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./add-domain-modal.scss";

interface AddDomainModalProps {
  show: boolean;
  onClose: () => void;
  /** `domainId` is the verified domain, so the list can walk to its row and light it up. */
  onSave?: (domainId: string) => void;
}

interface AddDomainModalState {
  domainValue: string;
  loading: boolean;
  /** Where Verify stands. Owned by `SaveStatus`, never set directly. */
  verifyStatus: KuikaButtonStatus;
  error?: string;
  domainId?: string;
  dnsRecords: DnsRecordRow[];
  showDnsRecords: boolean;
  polling: boolean;
}

interface DnsRecordRow {
  type: string;
  name: string;
  value: string;
}

/** What Verify is doing, rather than the generic "Saving". */
const VERIFY_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Verifying", success: "Verified" };

class AddDomainModal extends React.Component<AddDomainModalProps, AddDomainModalState> {
  private pollingInterval?: NodeJS.Timeout;

  private readonly verifyStatus: SaveStatus = new SaveStatus((verifyStatus: KuikaButtonStatus): void => {
    this.setState({ verifyStatus });
  });

  constructor(props: AddDomainModalProps) {
    super(props);
    this.state = {
      domainValue: "",
      loading: false,
      verifyStatus: "idle",
      error: undefined,
      domainId: undefined,
      dnsRecords: [],
      showDnsRecords: false,
      polling: false
    };
  }

  componentWillUnmount() {
    this.stopPolling();
    this.verifyStatus.dispose();
  }

  resetState = () => {
    this.stopPolling();
    this.verifyStatus.reset();
    this.setState({
      domainValue: "",
      loading: false,
      error: undefined,
      domainId: undefined,
      dnsRecords: [],
      showDnsRecords: false,
      polling: false
    });
  };

  handleStartOver = () => {
    this.resetState();
  };

  stopPolling = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }
  };

  startPollingForDnsRecords = async () => {
    const { domainId } = this.state;
    if (!domainId) return;

    this.setState({ polling: true });

    this.pollingInterval = setInterval(async () => {
      try {
        const response = await CloudDomainService.GetDomainStatus(domainId);

        if (response && response.data) {
          const { dnsCnameHost, dnsCnameTarget } = response.data;

          if (dnsCnameHost && dnsCnameTarget) {
            const dnsRecords: DnsRecordRow[] = [
              {
                type: "CNAME",
                name: this.cleanDnsName(dnsCnameHost),
                value: this.cleanDnsValue(dnsCnameTarget)
              }
            ];

            this.setState({ dnsRecords, polling: false });
            this.stopPolling();
          }
        }
      } catch (error) {
        console.error("Error polling for DNS records:", error);
      }
    }, 5000);

    setTimeout(() => {
      if (this.state.polling) {
        this.stopPolling();
        this.setState({
          polling: false,
          error: "Timeout waiting for DNS records. Please try refreshing or contact support."
        });
      }
    }, 300000);
  };

  handleAddDomain = async () => {
    const { domainValue } = this.state;

    if (!domainValue) {
      this.setState({ error: "Please enter a domain" });
      return;
    }

    this.setState({ loading: true, error: undefined });

    try {
      const normalizedDomain: string = CloudDomainService.normalizeDomainForWildcard(domainValue);
      this.setState({ domainValue: normalizedDomain });

      const response: AxiosResponse<IStartDomainResponse> = await CloudDomainService.StartDomain(normalizedDomain);

      if (response && response.data) {
        const { id, hostname, dnsCnameHost, dnsCnameTarget } = response.data;

        const dnsRecords: DnsRecordRow[] = [];
        if (dnsCnameHost && dnsCnameTarget) {
          dnsRecords.push({
            type: "CNAME",
            name: this.cleanDnsName(dnsCnameHost),
            value: this.cleanDnsValue(dnsCnameTarget)
          });
        }

        this.setState({
          domainId: id,
          domainValue: hostname,
          dnsRecords,
          showDnsRecords: true
        });

        if (dnsRecords.length === 0) {
          this.startPollingForDnsRecords();
        }
      } else {
        this.setState({
          error: "Failed to add domain. Please try again."
        });
      }
    } catch (error: unknown) {
      this.setState({
        error: error instanceof Error ? error.message : "Failed to add domain"
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleVerify = async () => {
    const { domainId } = this.state;
    const { onSave, onClose } = this.props;

    if (!domainId) return;
    // A second press while the check runs or its ✓ is held would verify twice and cancel the close.
    if (!this.verifyStatus.canStart) return;

    this.verifyStatus.start();
    this.setState({ error: undefined });

    try {
      const response: AxiosResponse<IVerifyDomainResponse> = await CloudDomainService.VerifyDomain(domainId);

      if (response && response.data && response.data.success) {
        // The ✓ goes up first and the dialog leaves after it; the list then walks to the domain's row.
        this.verifyStatus.succeed((): void => {
          if (onSave) onSave(domainId);
          onClose();
        });
      } else {
        this.setState({
          error: "Domain verification failed. Please check DNS records."
        });
        this.verifyStatus.fail();
      }
    } catch (error: unknown) {
      this.setState({
        error: error instanceof Error ? error.message : "Failed to verify domain"
      });
      this.verifyStatus.fail();
    }
  };

  /**
   * Cancel, the X and Escape. Closing during the ✓ hold would reset the dialog and cancel the close
   * the success already scheduled (and the list's walk to the new row), so it is swallowed there;
   * the dialog leaves on its own a moment later.
   */
  handleClose = (): void => {
    if (this.verifyStatus.current === "success") return;
    this.props.onClose();
  };

  // Remove trailing dot from DNS values for better UX
  cleanDnsValue = (value: string): string => {
    if (!value) return value;
    return value.endsWith(".") ? value.slice(0, -1) : value;
  };

  // Strip base domain from DNS name to show only subdomain
  cleanDnsName = (fullName: string): string => {
    if (!fullName) return fullName;

    // Remove trailing dot first
    let cleaned = this.cleanDnsValue(fullName);

    // Get base domain from domainValue (remove wildcard prefix if present)
    const { domainValue } = this.state;
    let baseDomain = domainValue;
    if (baseDomain.startsWith("*.")) {
      baseDomain = baseDomain.substring(2);
    }

    // Remove base domain suffix if present
    const domainSuffix = `.${baseDomain}`;
    if (cleaned.endsWith(domainSuffix)) {
      cleaned = cleaned.slice(0, -domainSuffix.length);
    }

    return cleaned;
  };

  renderDnsRecordRow = (label: string, value: string, showCopy: boolean = true) => {
    const cleanedValue = this.cleanDnsValue(value);
    return (
      <div className="kuika-add-domain-modal__dns-row">
        <div className="kuika-add-domain-modal__dns-label">
          <KuikaTextMedium className="kuika-add-domain-modal__secondary-text">{label}</KuikaTextMedium>
        </div>
        <div className="kuika-add-domain-modal__dns-value">
          <KuikaCodeText className="kuika-add-domain-modal__dns-value-text" title={cleanedValue}>
            {cleanedValue}
          </KuikaCodeText>
          {showCopy && (
            <KuikaCopyButton
              value={cleanedValue}
              type="tertiary"
              color="neutral"
              size="sm"
              label={`Copy ${label.toLowerCase()}`}
            />
          )}
        </div>
      </div>
    );
  };

  render() {
    const { show } = this.props;
    const { domainValue, loading, verifyStatus, error, dnsRecords, showDnsRecords, polling } = this.state;
    // The ✓ hold counts as busy too: the dialog is about to leave on its own.
    const verifying: boolean = verifyStatus === "loading" || verifyStatus === "success";
    const hasDnsRecords: boolean = showDnsRecords && dnsRecords.length > 0;

    // KModalFooter needs an always-present commit, which the pre-verification state doesn't
    // have (Cancel is the only action until DNS records exist). That earlier state falls back
    // to the same k-modal-footer__row markup KModalFooter renders internally, so the two footers
    // are visually identical; once DNS records are up, Verify is the commit and Start over rides
    // in `children` next to it.
    const footer: JSX.Element = hasDnsRecords ? (
      <KModalFooter
        onCancel={this.handleClose}
        submitLabel="Verify"
        onSubmit={this.handleVerify}
        status={verifyStatus}
        statusLabels={VERIFY_STATUS_LABELS}
      >
        <KuikaButton
          type="tertiary"
          color="neutral"
          size="md"
          uppercase
          onClick={this.handleStartOver}
          aria-label="Start over with new domain"
          disabled={loading || verifying}
        >
          Start over
        </KuikaButton>
      </KModalFooter>
    ) : (
      <div className="k-modal-footer__row">
        <span className="k-modal-footer__start" />
        <span className="k-modal-footer__end">
          <KuikaButton
            type="tertiary"
            color="neutral"
            size="md"
            uppercase
            onClick={this.handleClose}
            aria-label="Close modal"
            disabled={loading || verifying}
          >
            Cancel
          </KuikaButton>
        </span>
      </div>
    );

    return (
      <KModal showModal={show} closeModal={this.handleClose} header="Add domain" footer={footer} width={738}>
        <div className="kuika-add-domain-modal">
          <div className="kuika-add-domain-modal__section">
            <KuikaH4>Production</KuikaH4>
            <KuikaText className="kuika-add-domain-modal__secondary-text">
              Add and verify your custom domain here to deploy your application in the production environment.
            </KuikaText>
          </div>

          <div className="kuika-add-domain-modal__fields-row">
            <KuikaInput
              placeholder="https://www.kuika.com"
              value={domainValue}
              onChange={(_, v) => this.setState({ domainValue: String(v) })}
              aria-label="Domain URL"
              size="md"
              className="kuika-add-domain-modal__domain-input"
              disabled={loading || verifying || showDnsRecords}
            />
            <KuikaButton
              type="secondary"
              size="md"
              onClick={this.handleAddDomain}
              aria-label="Add domain"
              loading={loading}
              disabled={verifying || showDnsRecords}
            >
              ADD DOMAIN
            </KuikaButton>
          </div>

          {error && <KuikaInfoBox color="red" leftUnit={<KuikaText>{error}</KuikaText>} />}

          {hasDnsRecords && (
            <>
              <div className="kuika-add-domain-modal__section">
                <KuikaH6>DNS Configuration Required</KuikaH6>
                <KuikaText className="kuika-add-domain-modal__secondary-text">
                  Add the following DNS record to your domain provider's settings to verify domain ownership. Click the
                  copy icon to easily copy the values.
                </KuikaText>
              </div>

              {dnsRecords.map((record, index) => (
                <div key={`${record.type}-${record.name}-${index}`} className="kuika-add-domain-modal__dns-table">
                  {this.renderDnsRecordRow("Type", record.type, false)}
                  {this.renderDnsRecordRow("Name", record.name, true)}
                  {this.renderDnsRecordRow("Value", record.value, true)}
                </div>
              ))}

              <KuikaInfoBox
                color="blue"
                leftUnit={
                  <span className="kuika-add-domain-modal__notice">
                    <KuikaSvg name="info-circle" width={16} height={16} />
                    <KuikaText>
                      <strong>Next steps:</strong> After adding the DNS record to your domain provider, it may take a
                      few minutes to several hours for changes to propagate. Once updated, click 'VERIFY' to complete
                      the setup.
                    </KuikaText>
                  </span>
                }
              />
            </>
          )}

          {showDnsRecords && dnsRecords.length === 0 && (
            <KuikaInfoBox
              color="gray"
              leftUnit={
                <span className="kuika-add-domain-modal__notice">
                  {polling && <KuikaSmallSpinner spinning />}
                  <span className="kuika-add-domain-modal__notice-text">
                    <KuikaTextMedium>
                      {polling ? "Generating DNS records..." : "DNS records not available yet"}
                    </KuikaTextMedium>
                    {polling && (
                      <KuikaText className="kuika-add-domain-modal__secondary-text">
                        Please wait while we set up your domain configuration. This usually takes a few moments.
                      </KuikaText>
                    )}
                  </span>
                </span>
              }
            />
          )}
        </div>
      </KModal>
    );
  }
}

export default AddDomainModal;
